import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Film, ArrowLeft, Users, Loader2, AlertCircle, KeyRound, UserCheck } from 'lucide-react';
import { useRoomStore } from '@/store/roomStore';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { generateParticipantId } from '@/lib/utils';
import { PWAInstallButton } from '@/components/common/PWAInstallButton';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { AuthModal } from '@/components/auth/AuthModal';
import { CinematicIntroModal, CINESYNC_INTRO_SEEN_KEY } from '@/components/common/CinematicIntroModal';

export function JoinRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') || undefined;

  const navigate = useNavigate();
  const { setRoom, setParticipant } = useRoomStore();
  const { user } = useAuth();

  const [hasSeenIntro, setHasSeenIntro] = useState<boolean>(() => {
    try {
      if (!inviteToken) return true;
      return Boolean(sessionStorage.getItem(`cinesync_intro_dismissed_${inviteToken}`));
    } catch {
      return false;
    }
  });
  // Only show intro to account creation invitation link clickers (with invite token) on their first visit
  const [showIntro, setShowIntro] = useState<boolean>(() => Boolean(inviteToken) && !hasSeenIntro && !user);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const [name, setName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-fill participant name if signed in
  useEffect(() => {
    if (user && !name) {
      setName(user.displayName || user.email?.split('@')[0] || '');
    }
  }, [user, name]);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setHasSeenIntro(true);
    if (inviteToken) {
      try {
        sessionStorage.setItem(`cinesync_intro_dismissed_${inviteToken}`, 'true');
        localStorage.setItem(CINESYNC_INTRO_SEEN_KEY, 'true');
      } catch {}
    }
    // When video ends or user skips, automatically open account creation / sign in view
    if (!user) {
      setIsAuthModalOpen(true);
    }
  };

  const handleJoin = async () => {
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!roomId) {
      setError('Invalid room link.');
      return;
    }

    setError(null);
    setIsJoining(true);

    try {
      const participantId = generateParticipantId();
      setParticipant(participantId, name.trim(), 'guest');

      setRoom({
        roomId,
        hostId: '',
        movieTitle: 'Connecting to room…',
        movieDuration: 0,
        playbackState: 'paused',
        playbackRate: 1,
        currentTime: 0,
        lastActionTime: Date.now(),
        lastActionBy: '',
        version: 0,
        participants: [
          {
            id: participantId,
            name: name.trim(),
            role: 'guest',
            joinedAt: Date.now(),
            connectionState: 'connecting',
          },
        ],
        createdAt: Date.now(),
      });

      navigate(`/room/${roomId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to join room');
      setIsJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-cinema-950 text-cinema-100 flex flex-col font-sans">
      <header className="hairline-b bg-cinema-900/80 px-6 h-16 flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-xs font-medium text-cinema-300 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp rounded"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>

        <div className="flex items-center gap-3">
          <PWAInstallButton />
          <ThemeToggle showDropdown />
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-lamp" />
            <span className="text-xs uppercase tracking-wider text-cinema-300 font-semibold hidden xs:inline">Join Room</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full bg-cinema-900 border border-cinema-700/80 rounded-xl p-8 shadow-cinema-screen">
          <div className="mb-6 pb-4 hairline-b">
            <h1 className="text-xl font-bold text-cinema-100 tracking-tight">Join Watch Party</h1>
            <p className="text-xs text-cinema-400 mt-1">
              You are joining room <span className="font-mono text-cinema-200">{roomId}</span>
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <label htmlFor="join-name-input" className="block text-xs font-semibold uppercase tracking-wider text-cinema-300 mb-1.5">
                Your Name
              </label>
              <input
                id="join-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                placeholder="e.g. Robin"
                maxLength={24}
                autoFocus
                className="w-full bg-cinema-950 border border-cinema-700 rounded-md px-3.5 py-2.5 text-sm text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
              />
            </div>

            <div className="p-3 rounded bg-cinema-950/70 border border-cinema-700/50 text-xs text-cinema-300 leading-relaxed">
              No movie download needed. Once you join, you will stream the movie directly from the host. You can pause, play, or rewind the movie for everyone at any time.
            </div>

            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 p-2.5 rounded flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {user ? (
              <div className="flex items-center gap-2 text-xs text-reel-emerald bg-reel-emerald/10 border border-reel-emerald/20 px-3 py-2 rounded-md">
                <UserCheck className="w-3.5 h-3.5" />
                <span>Signed in as <strong className="font-medium text-cinema-100">{user.email}</strong></span>
              </div>
            ) : (
              <div className="flex items-center justify-between text-xs pt-1">
                <span className="text-cinema-400">Want to host your own parties?</span>
                <button
                  type="button"
                  onClick={() => setIsAuthModalOpen(true)}
                  className="text-lamp hover:underline font-semibold flex items-center gap-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp rounded"
                >
                  <KeyRound className="w-3 h-3" />
                  <span>{inviteToken ? 'Register Account' : 'Host Sign In'}</span>
                </button>
              </div>
            )}

            <button
              onClick={handleJoin}
              disabled={isJoining || !name.trim()}
              className="w-full py-3 px-4 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-40 disabled:cursor-not-allowed text-cinema-950 font-bold text-sm tracking-wide transition-all duration-150 enabled:active:scale-[0.96] shadow-md flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            >
              {isJoining ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Joining room…</span>
                </>
              ) : (
                <>
                  <Users className="w-4 h-4" />
                  <span>Join Room</span>
                </>
              )}
            </button>
          </div>
        </div>
      </main>

      {/* Fullscreen Cinematic Entrance for invite arrivals */}
      <CinematicIntroModal
        isOpen={showIntro && !user}
        onComplete={handleIntroComplete}
      />

      {/* Account Creation / Host Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onSuccess={() => {
          setIsAuthModalOpen(false);
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            setName(currentUser.displayName || currentUser.email?.split('@')[0] || '');
          }
        }}
        invitationToken={inviteToken}
        initialMode={inviteToken ? 'signup' : 'signin'}
      />
    </div>
  );
}
